import { useEffect, useState } from 'react';
import { matchPath, useLocation } from 'react-router-dom';
// @mui
import { Collapse } from '@mui/material';
// hooks
import useActiveLink from '../../../hooks/useActiveLink';
//
import { NavListProps } from '../types';
import NavItem from './NavItem';
import { StyledGroup } from './styles';

// ----------------------------------------------------------------------

type NavListRootProps = {
  data: NavListProps;
  depth: number;
  hasChild: boolean;
};

export default function NavList({ data, depth, hasChild }: NavListRootProps) {
  const { pathname } = useLocation();

  const { active, isExternalLink } = useActiveLink(data.path);

  // Check if any child is active by checking pathname match
  const isChildActive = (children: NavListProps[] | undefined): boolean => {
    if (!children) return false;
    return children.some((child) => {
      const childActive = child.path
        ? !!matchPath({ path: child.path, end: false }, pathname)
        : false;
      return childActive || isChildActive(child.children);
    });
  };

  const hasActiveChild =
    hasChild && data.children ? isChildActive(data.children) : false;
  const isActive = active || hasActiveChild;

  const [open, setOpen] = useState(isActive);

  useEffect(() => {
    if (isActive) {
      setOpen(true);
    } else if (!active) {
      handleClose();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pathname]);

  const handleToggle = () => {
    setOpen(!open);
  };

  const handleClose = () => {
    setOpen(false);
  };

  const item = (
    <>
      <NavItem
        item={data}
        depth={depth}
        open={open}
        active={isActive}
        isExternalLink={isExternalLink}
        onClick={handleToggle}
      />

      {hasChild && (
        <Collapse in={open} unmountOnExit>
          <NavSubList data={data.children} depth={depth} />
        </Collapse>
      )}
    </>
  );

  return hasChild && depth === 1 ? (
    <StyledGroup open={open}>{item}</StyledGroup>
  ) : (
    item
  );
}

// ----------------------------------------------------------------------

type NavListSubProps = {
  data: NavListProps[];
  depth: number;
};

function NavSubList({ data, depth }: NavListSubProps) {
  return (
    <>
      {data.map((list) => (
        <NavList
          key={list.title + list.path}
          data={list}
          depth={depth + 1}
          hasChild={!!list.children}
        />
      ))}
    </>
  );
}
